import React from "react";
import { Link } from "react-router-dom";
import Icon from "src/components/Icon/Icon";
import SlideUpAnimation from "../../components/SlideUpAnimation/SlideUpAnimation";

const BUYER_FEATURES = [
  {
    title: "Exclusive sales stock",
    description:
      "Apartments, townhouses, and new developments across prime London — many available off-market through NVRE.",
    icon: "home" as const,
  },
  {
    title: "Buy with confidence",
    description:
      "Comparable sales insight, area guides, and honest advice from agents who know London inside out.",
    icon: "location_on" as const,
  },
  {
    title: "Finance & tax tools",
    description:
      "Stamp duty and mortgage calculators built in, so you understand the full cost before you offer.",
    icon: "gavel" as const,
  },
];

const ALSO_SERVE = [
  {
    label: "Renters",
    eyebrow: "Search live listings",
    description: "Browse rentals with affordability tools, area intel, and live availability.",
    to: "/services/rent",
    accent: "border-text-obsidian",
  },
  {
    label: "Landlords",
    eyebrow: "Lettings & returns",
    description: "Rent estimates, yield tools, and compliance support for London landlords.",
    to: "/services/landlords",
    accent: "border-brand-yellow",
  },
];

export default function BuyersSection(): JSX.Element {
  return (
    <section className="bg-bg-white py-16 md:py-24 border-b border-solid border-[rgba(0,0,0,0.06)]">
      <div className="max-w-[1440px] mx-auto px-[5%]">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-start">
          <div className="pl-4 border-l-4 border-l-solid border-l-brand-yellow">
            <SlideUpAnimation triggerOnce>
              <span className="font-manrope text-xs tracking-wider uppercase text-text-slate block mb-2">
                Buyers
              </span>
              <h2 className="page-section-heading mb-4">
                Own your next London home
              </h2>
              <p className="font-manrope text-sm md:text-base text-text-slate leading-relaxed max-w-xl mb-6">
                Letting Engine is built around buyers first. Property sales are
                delivered through our sister agency, NVRE — exclusive homes,
                expert negotiation, and the same premium service across
                Kensington, Chelsea, Westminster, and beyond.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <Link
                  to="/services/buy"
                  className="inline-flex items-center gap-2 font-teko text-lg uppercase px-6 py-3 bg-brand-yellow text-text-obsidian hover:bg-brand-gold transition-colors no-underline"
                >
                  Explore sales
                  <Icon name="arrow_forward" size={18} />
                </Link>
                <a
                  href="https://nvre.co.uk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-teko text-lg uppercase px-6 py-3 border-2 border-solid border-text-obsidian text-text-obsidian hover:bg-text-obsidian hover:text-white transition-colors no-underline"
                >
                  nvre.co.uk
                  <Icon name="open_in_new" size={18} />
                </a>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link
                  to="/services/stamp-duty-calculator?language=english"
                  className="text-xs font-manrope uppercase tracking-wider bg-bg-porcelain px-3 py-1.5 border border-solid border-[rgba(0,0,0,0.08)] text-text-slate hover:border-brand-yellow hover:text-text-obsidian transition-colors no-underline"
                >
                  Stamp duty
                </Link>
                <Link
                  to="/services/mortgage-calculator"
                  className="text-xs font-manrope uppercase tracking-wider bg-bg-porcelain px-3 py-1.5 border border-solid border-[rgba(0,0,0,0.08)] text-text-slate hover:border-brand-yellow hover:text-text-obsidian transition-colors no-underline"
                >
                  Mortgage
                </Link>
                <Link
                  to="/guides/areas"
                  className="text-xs font-manrope uppercase tracking-wider bg-bg-porcelain px-3 py-1.5 border border-solid border-[rgba(0,0,0,0.08)] text-text-slate hover:border-brand-yellow hover:text-text-obsidian transition-colors no-underline"
                >
                  Area guides
                </Link>
              </div>
            </SlideUpAnimation>
          </div>

          <div className="flex flex-col gap-4">
            {BUYER_FEATURES.map((feature, index) => (
              <SlideUpAnimation
                key={feature.title}
                triggerOnce
                delay={index * 0.12}
              >
                <div className="flex gap-4 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.06)] p-5 md:p-6 h-full">
                  <div className="w-11 h-11 shrink-0 rounded-full bg-brand-yellow/25 flex items-center justify-center">
                    <Icon name={feature.icon} size={20} />
                  </div>
                  <div>
                    <h3 className="font-teko text-xl text-text-obsidian mb-1.5">
                      {feature.title}
                    </h3>
                    <p className="font-manrope text-sm text-text-slate leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </SlideUpAnimation>
            ))}
          </div>
        </div>

        <div className="mt-14 pt-10 border-t border-solid border-[rgba(0,0,0,0.08)]">
          <SlideUpAnimation triggerOnce>
            <p className="font-manrope text-xs uppercase tracking-wider text-text-slate mb-6 text-center md:text-left">
              We also support
            </p>
          </SlideUpAnimation>
          <div className="grid md:grid-cols-2 gap-4">
            {ALSO_SERVE.map((item, index) => (
              <SlideUpAnimation key={item.label} triggerOnce delay={index * 0.1}>
                <Link
                  to={item.to}
                  className={`group flex flex-col h-full p-5 md:p-6 bg-bg-porcelain border border-solid border-[rgba(0,0,0,0.06)] border-l-4 ${item.accent} hover:bg-white hover:shadow-soft transition-all no-underline text-inherit`}
                >
                  <span className="font-manrope text-[10px] uppercase tracking-[0.2em] text-text-slate mb-1">
                    {item.eyebrow}
                  </span>
                  <h3 className="font-teko text-2xl text-text-obsidian mb-2 group-hover:text-brand-yellow transition-colors">
                    {item.label}
                  </h3>
                  <p className="font-manrope text-sm text-text-slate leading-relaxed flex-1">
                    {item.description}
                  </p>
                  <span className="inline-flex items-center gap-1.5 font-manrope text-xs uppercase tracking-wider text-text-obsidian mt-4 group-hover:gap-2.5 transition-all">
                    Learn more
                    <Icon name="arrow_forward" size={14} />
                  </span>
                </Link>
              </SlideUpAnimation>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
